import React, { useState, useEffect } from 'react';

import './index.css';
import axios from '../../store/axios';
import logo from '../../static/img/logo.png';

const PlanOffer = ({ match, location }) => {
  const token = match['params']['token'];
  const params = new URLSearchParams(location['search']);
  const isSuccess = params.get('status') === 'success';
  const ws = params.get('ws') || '';
  const wsQuery = ws ? `?ws=${ws}` : '';

  const [ui, setUi] = useState({ loading: true, paying: false, notFound: false, error: '', plan: null });
  const updateUi = (patch) => setUi(prev => ({ ...prev, ...patch }));

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await axios.get(`/payment/custom-plan/${token}${wsQuery}`);
        if (response['data']['code'] === 200) updateUi({ loading: false, plan: response['data']['body'] });
        else updateUi({ loading: false, notFound: true });
      } catch (error) {
        updateUi({ loading: false, notFound: true });
      }
    };
    fetchPlan();
  }, [token, wsQuery]);

  const handlePay = async () => {
    updateUi({ paying: true, error: '' });
    try {
      const response = await axios.post(`/payment/custom-plan/${token}/checkout${wsQuery}`);
      if (response['data']['code'] === 200 && response['data']['body']?.['url']) window.location = response['data']['body']['url'];
      else updateUi({ paying: false, error: response['data']['message'] || 'Unable to start checkout. Please try again.' });
    } catch (error) {
      updateUi({ paying: false, error: error['response']?.['data']?.['message'] || 'Unable to start checkout. Please try again.' });
    }
  };

  const plan = ui['plan'];

  return (
    <div className='plan-offer-page'>
      <div className='plan-offer-header'>
        <img alt='Logo' src={logo} className='plan-offer-logo' />
      </div>

      {ui['loading'] ? (
        <div className='plan-offer-card plan-offer-center'>
          <i className='fa fa-spinner fa-spin plan-offer-spinner'></i>
        </div>
      ) : isSuccess ? (
        <div className='plan-offer-card plan-offer-center'>
          <i className='fa fa-check-circle plan-offer-success-icon'></i>
          <h3 className='plan-offer-title'>Payment received!</h3>
          <p className='plan-offer-text'>Your plan is being activated. Check your email for confirmation and next steps.</p>
        </div>
      ) : ui['notFound'] || !plan ? (
        <div className='plan-offer-card plan-offer-center'>
          <i className='fa fa-info-circle plan-offer-muted-icon'></i>
          <h3 className='plan-offer-title'>Plan not found</h3>
          <p className='plan-offer-text'>This plan link is invalid or no longer available. Please contact your account manager.</p>
        </div>
      ) : (
        <div className='plan-offer-card'>
          <div className='plan-offer-hero'>
            <span className='plan-offer-eyebrow'>Your plan</span>
            <h5 className='plan-offer-name'>{plan['displayName']}</h5>
            <div className='plan-offer-price'>
              <span className='plan-offer-price-amount'>${Number(plan['price']).toLocaleString()}</span>
              <span className='plan-offer-price-interval'>/{plan['interval'] || 'month'}</span>
            </div>
            {plan['discountPercent'] > 0 && (
              <div className='plan-offer-discount'>
                <i className='fa fa-tag'></i>
                <span>{plan['discountPercent']}% off your first month — applied automatically</span>
              </div>
            )}
          </div>

          <div className='plan-offer-body'>
            {plan['description'] && <p className='plan-offer-desc'>{plan['description']}</p>}

            {plan['targetWorkspace'] && (
              <div className='plan-offer-workspace'>
                <i className='fa fa-building'></i>
                <span>Added to your <b>{plan['targetWorkspace']['name']}</b> workspace</span>
              </div>
            )}

            {(plan['featuresWithIcons'] || []).filter(feature => feature['text']).length > 0 && (
              <>
                <div className='plan-offer-features-title'>What's included</div>
                <ul className='plan-offer-features'>
                  {(plan['featuresWithIcons'] || []).filter(feature => feature['text']).map((feature, idx) => (
                    <li key={idx}><i className='fa fa-check'></i><span>{feature['text']}</span></li>
                  ))}
                </ul>
              </>
            )}

            {plan['isPurchased'] ? (
              <div className='plan-offer-status'>This plan has already been purchased.</div>
            ) : !plan['isAvailable'] ? (
              <div className='plan-offer-status'>This plan is not currently available. Please contact your account manager.</div>
            ) : (
              <>
                <button className='plan-offer-pay-btn' onClick={handlePay} disabled={ui['paying']}>
                  {ui['paying'] ? (
                    <><i className='fa fa-spinner fa-spin mr-2' />Redirecting to secure checkout…</>
                  ) : (
                    <><i className='fa fa-lock mr-2'></i>Pay ${Number(plan['price']).toLocaleString()} with Stripe</>
                  )}
                </button>
                {ui['error'] && <div className='plan-offer-error'><i className='fa fa-exclamation-circle mr-1'></i>{ui['error']}</div>}

                <div className='plan-offer-trust'>
                  <span><i className='fa fa-lock'></i>256-bit secure</span>
                  <span><i className='fa fa-bolt'></i>Instant activation</span>
                  <span><i className='fa fa-refresh'></i>Cancel anytime</span>
                </div>
              </>
            )}
          </div>

          <p className='plan-offer-footnote'>
            <i className='fa fa-shield mr-1'></i>
            Payments securely powered by Stripe. {plan['targetWorkspace'] ? 'Applied to your workspace immediately after payment.' : 'Your workspace is created automatically after payment.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default PlanOffer;
